import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  Check,
  X,
  Sparkles,
  Users,
  GraduationCap,
  Building2,
  User,
  ArrowRight,
  Info,
  Loader2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { featureFlags } from "@/config/featureFlags";
import logo from "@/assets/ithopu-logo.png";
import iTHOPULogo from "@/assets/iTHOPU.png";

// Map plan names to Chargebee item_price_ids
const PLAN_IDS: Record<string, { monthly: string; annual: string }> = {
  individual: { monthly: "individual-monthly", annual: "individual-yearly" },
  parent: { monthly: "parent-monthly", annual: "parent-yearly" },
  teacher: { monthly: "teacher-monthly", annual: "teacher-yearly" },
};

export default function Pricing() {
  const [billingCycle, setBillingCycle] = useState<"monthly" | "annual">(
    "monthly",
  );
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleLaunchApp = () => {
    // Marketing site (ithopu.com) → redirect to app subdomain
    if (
      window.location.hostname === "ithopu.com" ||
      window.location.hostname === "www.ithopu.com"
    ) {
      window.location.href = "https://app.ithopu.com";
    }
    // App subdomain or localhost → go to dashboard (will redirect to login if not authenticated)
    else {
      window.location.href = "/dashboard";
    }
  };

  const handleGetStarted = async (plan: string) => {
    // Free plan → just register/dashboard
    if (plan === "free" || plan === "trial") {
      if (user) {
        navigate("/dashboard");
      } else {
        navigate("/register");
      }
      return;
    }

    // When Chargebee billing is disabled, paid plan buttons just navigate
    if (!featureFlags.CHARGEBEE_BILLING) {
      navigate(user ? "/dashboard" : "/register");
      return;
    }

    // Paid plan — need to be logged in
    if (!user) {
      // Store the desired plan so we can redirect after login/register
      const planId = PLAN_IDS[plan]?.[billingCycle === "annual" ? "annual" : "monthly"];
      if (planId) {
        sessionStorage.setItem("checkout_plan", planId);
      }
      navigate("/register");
      return;
    }

    // Already logged in → create checkout session
    const planId = PLAN_IDS[plan]?.[billingCycle === "annual" ? "annual" : "monthly"];
    if (!planId) {
      navigate("/dashboard");
      return;
    }

    setLoadingPlan(plan);
    try {
      const { url } = await api.createCheckoutSession(planId);
      window.location.href = url;
    } catch (err: any) {
      toast.error(err?.message || "Failed to start checkout");
    } finally {
      setLoadingPlan(null);
    }
  };

  const handleContactSales = () => {
    window.location.href = "mailto:ithopu@ithopu.com?subject=School%20%26%20Enterprise%20Inquiry";
  };

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-900">
      {/* Navigation */}
      <nav className="border-b border-zinc-200 dark:border-zinc-800 bg-white/80 dark:bg-zinc-900/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-8">
              <a href="/" className="flex items-center gap-2">
                <img src={logo} alt="Ithopu" className="w-10 h-10" />
                <img src={iTHOPULogo} alt="Ithopu" className="h-8" />
              </a>
              <div className="hidden md:flex items-center gap-6">
                <a
                  href="/#features"
                  className="text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white transition-colors"
                >
                  Features
                </a>
                <a
                  href="/pricing"
                  className="text-zinc-900 dark:text-white font-medium"
                >
                  Pricing
                </a>
                <a
                  href="/blog"
                  className="text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white transition-colors"
                >
                  Blog
                </a>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button onClick={handleLaunchApp}>
                Launch App
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="container mx-auto px-4 py-16 md:py-24">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-block mb-4 px-4 py-2 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-sm font-medium">
            Simple, Transparent Pricing
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-zinc-900 dark:text-white mb-6 leading-tight">
            Choose the Perfect Plan for{" "}
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 bg-clip-text text-transparent">
              Your Learning Journey
            </span>
          </h1>
          <p className="text-xl text-zinc-600 dark:text-zinc-400 mb-8 max-w-3xl mx-auto">
            Start with a 30-day free trial. Full access to all features, no
            credit card required. Cancel anytime.
          </p>

          {/* Billing Cycle Toggle */}
          <div className="flex items-center justify-center gap-4 mb-12">
            <span
              className={`text-lg font-medium ${
                billingCycle === "monthly"
                  ? "text-zinc-900 dark:text-white"
                  : "text-zinc-500 dark:text-zinc-400"
              }`}
            >
              Monthly
            </span>
            <button
              onClick={() =>
                setBillingCycle(
                  billingCycle === "monthly" ? "annual" : "monthly",
                )
              }
              className="relative w-16 h-8 bg-zinc-200 dark:bg-zinc-700 rounded-full transition-colors hover:bg-zinc-300 dark:hover:bg-zinc-600"
            >
              <div
                className={`absolute top-1 left-1 w-6 h-6 bg-blue-600 rounded-full transition-transform ${
                  billingCycle === "annual" ? "translate-x-8" : ""
                }`}
              />
            </button>
            <span
              className={`text-lg font-medium ${
                billingCycle === "annual"
                  ? "text-zinc-900 dark:text-white"
                  : "text-zinc-500 dark:text-zinc-400"
              }`}
            >
              Annual
              <span className="ml-2 text-sm px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full">
                Save 17%
              </span>
            </span>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="container mx-auto px-4 pb-20">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
          {/* Free Plan */}
          <Card className="border-2 border-zinc-200 dark:border-zinc-700 hover:shadow-lg transition-all bg-white dark:bg-zinc-800">
            <CardHeader className="pt-6 pb-4">
              <div className="w-12 h-12 bg-zinc-100 text-zinc-600 dark:bg-zinc-700 dark:text-zinc-400 rounded-lg flex items-center justify-center mb-4">
                <User className="w-6 h-6" />
              </div>
              <h3 className="text-2xl font-bold text-zinc-900 dark:text-white">
                Free
              </h3>
              <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-4">
                Perfect for getting started
              </p>
              <div className="mb-4">
                <div className="text-4xl font-bold text-zinc-900 dark:text-white">
                  $0
                </div>
                <div className="text-sm text-zinc-600 dark:text-zinc-400">
                  forever
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <Button
                variant="outline"
                className="w-full mb-6"
                onClick={() => handleGetStarted("free")}
              >
                Get Started Free
              </Button>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-zinc-600 dark:text-zinc-400">
                    First 5-10 basic techniques
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-zinc-600 dark:text-zinc-400">
                    10 problems per day
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-zinc-600 dark:text-zinc-400">
                    Video tutorials
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-zinc-600 dark:text-zinc-400">
                    Basic progress tracking
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-zinc-600 dark:text-zinc-400">
                    Streak tracking
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <X className="w-5 h-5 text-zinc-400 dark:text-zinc-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-zinc-400 dark:text-zinc-500 line-through">
                    Advanced techniques
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <X className="w-5 h-5 text-zinc-400 dark:text-zinc-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-zinc-400 dark:text-zinc-500 line-through">
                    Tournaments
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <X className="w-5 h-5 text-zinc-400 dark:text-zinc-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-zinc-400 dark:text-zinc-500 line-through">
                    Advanced analytics
                  </span>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Individual Plan - Most Popular */}
          <Card className="border-2 border-blue-400 dark:border-blue-600 hover:shadow-xl transition-all bg-gradient-to-br from-blue-50 to-white dark:from-blue-950 dark:to-zinc-900 relative">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-full text-sm font-semibold shadow-lg">
              Most Popular
            </div>
            <CardHeader className="pt-10 pb-4">
              <div className="w-12 h-12 bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 rounded-lg flex items-center justify-center mb-4">
                <Sparkles className="w-6 h-6" />
              </div>
              <h3 className="text-2xl font-bold text-zinc-900 dark:text-white">
                Individual
              </h3>
              <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-4">
                For students learning independently
              </p>
              <div className="mb-4">
                <div className="text-4xl font-bold text-zinc-900 dark:text-white">
                  ${billingCycle === "monthly" ? "9.99" : "8.33"}
                </div>
                <div className="text-sm text-zinc-600 dark:text-zinc-400">
                  per month
                  {billingCycle === "annual" && " (billed annually)"}
                </div>
                {billingCycle === "annual" && (
                  <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                    $99.99/year - Save $19.89
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <Button
                className="w-full mb-6 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                onClick={() => handleGetStarted("individual")}
                disabled={loadingPlan === "individual"}
              >
                {loadingPlan === "individual" ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing...</>
                ) : (
                  <>Start Free Trial<ArrowRight className="w-4 h-4 ml-2" /></>
                )}
              </Button>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-zinc-600 dark:text-zinc-400">
                    All 60+ techniques unlocked
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-zinc-600 dark:text-zinc-400">
                    Unlimited daily practice
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-zinc-600 dark:text-zinc-400">
                    Tournaments & Beat the Clock
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-zinc-600 dark:text-zinc-400">
                    Advanced analytics
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-zinc-600 dark:text-zinc-400">
                    Learning paths & roadmaps
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-zinc-600 dark:text-zinc-400">
                    Full badge system
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-zinc-600 dark:text-zinc-400">
                    Global leaderboards
                  </span>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Parent Plan */}
          <Card className="border-2 border-purple-200 dark:border-purple-800 hover:shadow-lg transition-all bg-gradient-to-br from-purple-50 to-white dark:from-purple-950 dark:to-zinc-900">
            <CardHeader className="pt-6 pb-4">
              <div className="w-12 h-12 bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400 rounded-lg flex items-center justify-center mb-4">
                <Users className="w-6 h-6" />
              </div>
              <h3 className="text-2xl font-bold text-zinc-900 dark:text-white">
                Parent
              </h3>
              <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-4">
                Monitor your children's progress
              </p>
              <div className="mb-4">
                <div className="text-4xl font-bold text-zinc-900 dark:text-white">
                  ${billingCycle === "monthly" ? "14.99" : "12.49"}
                </div>
                <div className="text-sm text-zinc-600 dark:text-zinc-400">
                  per month (2 children)
                </div>
                {billingCycle === "annual" && (
                  <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                    $149.99/year - Save $29.89
                  </div>
                )}
                <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-2">
                  +$5/month per additional child
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <Button
                variant="outline"
                className="w-full mb-6 border-purple-300 dark:border-purple-700 hover:bg-purple-50 dark:hover:bg-purple-900/20"
                onClick={() => handleGetStarted("parent")}
                disabled={loadingPlan === "parent"}
              >
                {loadingPlan === "parent" ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing...</>
                ) : (
                  "Start Free Trial"
                )}
              </Button>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-zinc-600 dark:text-zinc-400">
                    All Individual features
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-zinc-600 dark:text-zinc-400">
                    Parent portal access
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-zinc-600 dark:text-zinc-400">
                    Monitor all children
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-zinc-600 dark:text-zinc-400">
                    Compare progress across children
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-zinc-600 dark:text-zinc-400">
                    Progress reports
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-zinc-600 dark:text-zinc-400">
                    Weekly email summaries
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-zinc-600 dark:text-zinc-400">
                    Up to 4 children
                  </span>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Teacher Plan */}
          <Card className="border-2 border-orange-200 dark:border-orange-800 hover:shadow-lg transition-all bg-gradient-to-br from-orange-50 to-white dark:from-orange-950 dark:to-zinc-900">
            <CardHeader className="pt-6 pb-4">
              <div className="w-12 h-12 bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400 rounded-lg flex items-center justify-center mb-4">
                <GraduationCap className="w-6 h-6" />
              </div>
              <h3 className="text-2xl font-bold text-zinc-900 dark:text-white">
                Teacher
              </h3>
              <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-4">
                Manage your classroom
              </p>
              <div className="mb-4">
                <div className="text-4xl font-bold text-zinc-900 dark:text-white">
                  ${billingCycle === "monthly" ? "59" : "49.17"}
                </div>
                <div className="text-sm text-zinc-600 dark:text-zinc-400">
                  per classroom/month
                </div>
                {billingCycle === "annual" && (
                  <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                    $590/year
                  </div>
                )}
                <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-2">
                  Up to 10 students per classroom
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <Button
                variant="outline"
                className="w-full mb-6 border-orange-300 dark:border-orange-700 hover:bg-orange-50 dark:hover:bg-orange-900/20"
                onClick={() => handleGetStarted("teacher")}
                disabled={loadingPlan === "teacher"}
              >
                {loadingPlan === "teacher" ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing...</>
                ) : (
                  "Start Free Trial"
                )}
              </Button>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-zinc-600 dark:text-zinc-400">
                    All premium features
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-zinc-600 dark:text-zinc-400">
                    Classroom management
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-zinc-600 dark:text-zinc-400">
                    Create assignments
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-zinc-600 dark:text-zinc-400">
                    Auto-grading
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-zinc-600 dark:text-zinc-400">
                    Class analytics
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-zinc-600 dark:text-zinc-400">
                    Private tournaments
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-zinc-600 dark:text-zinc-400">
                    Student progress tracking
                  </span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* School/Enterprise Plan - Full Width */}
        <Card className="border-2 border-cyan-200 dark:border-cyan-800 hover:shadow-lg transition-all bg-gradient-to-br from-cyan-50 to-white dark:from-cyan-950 dark:to-zinc-900 max-w-7xl mx-auto mt-6">
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-cyan-100 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-400 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-8 h-8" />
                </div>
                <div>
                  <h3 className="text-3xl font-bold text-zinc-900 dark:text-white mb-2">
                    School & Enterprise
                  </h3>
                  <p className="text-lg text-zinc-600 dark:text-zinc-400 mb-4">
                    Custom pricing for schools and large organizations
                  </p>
                  <ul className="grid md:grid-cols-2 gap-x-8 gap-y-2">
                    <li className="flex items-center gap-2">
                      <Check className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                      <span className="text-sm text-zinc-600 dark:text-zinc-400">
                        Multiple teachers & classrooms
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                      <span className="text-sm text-zinc-600 dark:text-zinc-400">
                        School-wide analytics dashboard
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                      <span className="text-sm text-zinc-600 dark:text-zinc-400">
                        Bulk student account import
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                      <span className="text-sm text-zinc-600 dark:text-zinc-400">
                        Custom subdomain (school.ithopu.com)
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                      <span className="text-sm text-zinc-600 dark:text-zinc-400">
                        Branded certificates with school logo
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                      <span className="text-sm text-zinc-600 dark:text-zinc-400">
                        Priority support
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="flex flex-col gap-3 min-w-[200px]">
                <Button
                  size="lg"
                  className="bg-cyan-600 hover:bg-cyan-700"
                  onClick={handleContactSales}
                >
                  Contact Sales
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <p className="text-xs text-center text-zinc-500 dark:text-zinc-400">
                  Contact ithopu@ithopu.com
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Feature Comparison Table */}
      <section className="container mx-auto px-4 pb-20">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 dark:text-white mb-8 text-center">
            Compare Plans
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse bg-white dark:bg-zinc-800 rounded-lg overflow-hidden shadow-lg">
              <thead>
                <tr className="bg-zinc-100 dark:bg-zinc-900">
                  <th className="text-left p-4 font-semibold text-zinc-900 dark:text-white">
                    Features
                  </th>
                  <th className="text-center p-4 font-semibold text-zinc-900 dark:text-white">
                    Free
                  </th>
                  <th className="text-center p-4 font-semibold text-zinc-900 dark:text-white bg-blue-50 dark:bg-blue-950">
                    Individual
                  </th>
                  <th className="text-center p-4 font-semibold text-zinc-900 dark:text-white">
                    Parent
                  </th>
                  <th className="text-center p-4 font-semibold text-zinc-900 dark:text-white">
                    Teacher
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
                <tr>
                  <td className="p-4 text-zinc-700 dark:text-zinc-300">
                    Techniques Available
                  </td>
                  <td className="p-4 text-center text-zinc-600 dark:text-zinc-400">
                    5-10 basics
                  </td>
                  <td className="p-4 text-center bg-blue-50 dark:bg-blue-950 font-semibold text-zinc-900 dark:text-white">
                    60+ All
                  </td>
                  <td className="p-4 text-center font-semibold text-zinc-900 dark:text-white">
                    60+ All
                  </td>
                  <td className="p-4 text-center font-semibold text-zinc-900 dark:text-white">
                    60+ All
                  </td>
                </tr>
                <tr>
                  <td className="p-4 text-zinc-700 dark:text-zinc-300">
                    Daily Practice Limit
                  </td>
                  <td className="p-4 text-center text-zinc-600 dark:text-zinc-400">
                    10 problems
                  </td>
                  <td className="p-4 text-center bg-blue-50 dark:bg-blue-950 font-semibold text-zinc-900 dark:text-white">
                    Unlimited
                  </td>
                  <td className="p-4 text-center font-semibold text-zinc-900 dark:text-white">
                    Unlimited
                  </td>
                  <td className="p-4 text-center font-semibold text-zinc-900 dark:text-white">
                    Unlimited
                  </td>
                </tr>
                <tr>
                  <td className="p-4 text-zinc-700 dark:text-zinc-300">
                    Video Tutorials
                  </td>
                  <td className="p-4 text-center">
                    <Check className="w-5 h-5 text-green-600 dark:text-green-400 mx-auto" />
                  </td>
                  <td className="p-4 text-center bg-blue-50 dark:bg-blue-950">
                    <Check className="w-5 h-5 text-green-600 dark:text-green-400 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <Check className="w-5 h-5 text-green-600 dark:text-green-400 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <Check className="w-5 h-5 text-green-600 dark:text-green-400 mx-auto" />
                  </td>
                </tr>
                <tr>
                  <td className="p-4 text-zinc-700 dark:text-zinc-300">
                    Tournaments
                  </td>
                  <td className="p-4 text-center">
                    <X className="w-5 h-5 text-zinc-400 dark:text-zinc-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center bg-blue-50 dark:bg-blue-950">
                    <Check className="w-5 h-5 text-green-600 dark:text-green-400 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <Check className="w-5 h-5 text-green-600 dark:text-green-400 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <Check className="w-5 h-5 text-green-600 dark:text-green-400 mx-auto" />
                  </td>
                </tr>
                <tr>
                  <td className="p-4 text-zinc-700 dark:text-zinc-300">
                    Advanced Analytics
                  </td>
                  <td className="p-4 text-center">
                    <X className="w-5 h-5 text-zinc-400 dark:text-zinc-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center bg-blue-50 dark:bg-blue-950">
                    <Check className="w-5 h-5 text-green-600 dark:text-green-400 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <Check className="w-5 h-5 text-green-600 dark:text-green-400 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <Check className="w-5 h-5 text-green-600 dark:text-green-400 mx-auto" />
                  </td>
                </tr>
                <tr>
                  <td className="p-4 text-zinc-700 dark:text-zinc-300">
                    Learning Paths
                  </td>
                  <td className="p-4 text-center">
                    <X className="w-5 h-5 text-zinc-400 dark:text-zinc-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center bg-blue-50 dark:bg-blue-950">
                    <Check className="w-5 h-5 text-green-600 dark:text-green-400 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <Check className="w-5 h-5 text-green-600 dark:text-green-400 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <Check className="w-5 h-5 text-green-600 dark:text-green-400 mx-auto" />
                  </td>
                </tr>
                <tr>
                  <td className="p-4 text-zinc-700 dark:text-zinc-300">
                    Parent Portal
                  </td>
                  <td className="p-4 text-center">
                    <X className="w-5 h-5 text-zinc-400 dark:text-zinc-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center bg-blue-50 dark:bg-blue-950">
                    <X className="w-5 h-5 text-zinc-400 dark:text-zinc-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <Check className="w-5 h-5 text-green-600 dark:text-green-400 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <X className="w-5 h-5 text-zinc-400 dark:text-zinc-500 mx-auto" />
                  </td>
                </tr>
                <tr>
                  <td className="p-4 text-zinc-700 dark:text-zinc-300">
                    Classroom Management
                  </td>
                  <td className="p-4 text-center">
                    <X className="w-5 h-5 text-zinc-400 dark:text-zinc-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center bg-blue-50 dark:bg-blue-950">
                    <X className="w-5 h-5 text-zinc-400 dark:text-zinc-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <X className="w-5 h-5 text-zinc-400 dark:text-zinc-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <Check className="w-5 h-5 text-green-600 dark:text-green-400 mx-auto" />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="container mx-auto px-4 pb-20">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 dark:text-white mb-4 text-center">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-zinc-600 dark:text-zinc-400 mb-12 text-center">
            Everything you need to know about our pricing
          </p>

          <div className="space-y-6">
            <details className="group bg-white dark:bg-zinc-800 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow border border-zinc-200 dark:border-zinc-700">
              <summary className="flex justify-between items-center font-semibold text-lg cursor-pointer list-none text-zinc-900 dark:text-white">
                <span>How does the free trial work?</span>
                <span className="transition group-open:rotate-180 text-zinc-500 dark:text-zinc-400">
                  <svg
                    fill="none"
                    height="24"
                    shapeRendering="geometricPrecision"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.5"
                    viewBox="0 0 24 24"
                    width="24"
                  >
                    <path d="M6 9l6 6 6-6"></path>
                  </svg>
                </span>
              </summary>
              <p className="text-zinc-600 dark:text-zinc-400 mt-4 leading-relaxed">
                You get full access to all premium features for 30 days,
                absolutely free. No credit card required. After the trial, you
                can choose to upgrade to a paid plan or continue with our
                limited free tier.
              </p>
            </details>

            <details className="group bg-white dark:bg-zinc-800 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow border border-zinc-200 dark:border-zinc-700">
              <summary className="flex justify-between items-center font-semibold text-lg cursor-pointer list-none text-zinc-900 dark:text-white">
                <span>Can I cancel anytime?</span>
                <span className="transition group-open:rotate-180 text-zinc-500 dark:text-zinc-400">
                  <svg
                    fill="none"
                    height="24"
                    shapeRendering="geometricPrecision"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.5"
                    viewBox="0 0 24 24"
                    width="24"
                  >
                    <path d="M6 9l6 6 6-6"></path>
                  </svg>
                </span>
              </summary>
              <p className="text-zinc-600 dark:text-zinc-400 mt-4 leading-relaxed">
                Yes! You can cancel your subscription at any time. You'll
                continue to have access until the end of your current billing
                period, and then you'll be moved to the free tier.
              </p>
            </details>

            <details className="group bg-white dark:bg-zinc-800 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow border border-zinc-200 dark:border-zinc-700">
              <summary className="flex justify-between items-center font-semibold text-lg cursor-pointer list-none text-zinc-900 dark:text-white">
                <span>
                  What's the difference between monthly and annual plans?
                </span>
                <span className="transition group-open:rotate-180 text-zinc-500 dark:text-zinc-400">
                  <svg
                    fill="none"
                    height="24"
                    shapeRendering="geometricPrecision"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.5"
                    viewBox="0 0 24 24"
                    width="24"
                  >
                    <path d="M6 9l6 6 6-6"></path>
                  </svg>
                </span>
              </summary>
              <p className="text-zinc-600 dark:text-zinc-400 mt-4 leading-relaxed">
                Annual plans give you the same features as monthly plans, but at
                a discounted rate (save 17%). You pay once per year instead of
                monthly, and you save money in the long run.
              </p>
            </details>

            <details className="group bg-white dark:bg-zinc-800 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow border border-zinc-200 dark:border-zinc-700">
              <summary className="flex justify-between items-center font-semibold text-lg cursor-pointer list-none text-zinc-900 dark:text-white">
                <span>Can I upgrade or downgrade my plan?</span>
                <span className="transition group-open:rotate-180 text-zinc-500 dark:text-zinc-400">
                  <svg
                    fill="none"
                    height="24"
                    shapeRendering="geometricPrecision"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.5"
                    viewBox="0 0 24 24"
                    width="24"
                  >
                    <path d="M6 9l6 6 6-6"></path>
                  </svg>
                </span>
              </summary>
              <p className="text-zinc-600 dark:text-zinc-400 mt-4 leading-relaxed">
                Absolutely! You can upgrade or downgrade your plan at any time
                from your account settings. When upgrading, you'll be charged
                the prorated difference immediately. When downgrading, the
                changes take effect at the end of your current billing period.
              </p>
            </details>

            <details className="group bg-white dark:bg-zinc-800 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow border border-zinc-200 dark:border-zinc-700">
              <summary className="flex justify-between items-center font-semibold text-lg cursor-pointer list-none text-zinc-900 dark:text-white">
                <span>How many children can I add to the Parent plan?</span>
                <span className="transition group-open:rotate-180 text-zinc-500 dark:text-zinc-400">
                  <svg
                    fill="none"
                    height="24"
                    shapeRendering="geometricPrecision"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.5"
                    viewBox="0 0 24 24"
                    width="24"
                  >
                    <path d="M6 9l6 6 6-6"></path>
                  </svg>
                </span>
              </summary>
              <p className="text-zinc-600 dark:text-zinc-400 mt-4 leading-relaxed">
                The Parent plan includes 2 children by default at $14.99/month.
                You can add up to 2 more children (4 total) at $5/month per
                additional child. For families with more than 4 children, please
                contact us for a custom solution.
              </p>
            </details>

            <details className="group bg-white dark:bg-zinc-800 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow border border-zinc-200 dark:border-zinc-700">
              <summary className="flex justify-between items-center font-semibold text-lg cursor-pointer list-none text-zinc-900 dark:text-white">
                <span>How many students can I have in the Teacher plan?</span>
                <span className="transition group-open:rotate-180 text-zinc-500 dark:text-zinc-400">
                  <svg
                    fill="none"
                    height="24"
                    shapeRendering="geometricPrecision"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.5"
                    viewBox="0 0 24 24"
                    width="24"
                  >
                    <path d="M6 9l6 6 6-6"></path>
                  </svg>
                </span>
              </summary>
              <p className="text-zinc-600 dark:text-zinc-400 mt-4 leading-relaxed">
                Each Teacher plan subscription supports one classroom with up to
                10 students. If you have more students, you can purchase
                additional classroom subscriptions. For example, 20 students
                would require 2 classroom subscriptions at $118/month total.
              </p>
            </details>

            <details className="group bg-white dark:bg-zinc-800 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow border border-zinc-200 dark:border-zinc-700">
              <summary className="flex justify-between items-center font-semibold text-lg cursor-pointer list-none text-zinc-900 dark:text-white">
                <span>Do you offer discounts for schools?</span>
                <span className="transition group-open:rotate-180 text-zinc-500 dark:text-zinc-400">
                  <svg
                    fill="none"
                    height="24"
                    shapeRendering="geometricPrecision"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.5"
                    viewBox="0 0 24 24"
                    width="24"
                  >
                    <path d="M6 9l6 6 6-6"></path>
                  </svg>
                </span>
              </summary>
              <p className="text-zinc-600 dark:text-zinc-400 mt-4 leading-relaxed">
                Yes! Our School & Enterprise plan offers significant volume
                discounts with pricing as low as $1.50-2.50 per student per
                month. This includes unlimited teachers, unlimited classrooms,
                and school-wide analytics. Contact our sales team for a custom
                quote based on your school's size.
              </p>
            </details>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="container mx-auto px-4 pb-20">
        <div className="max-w-4xl mx-auto bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-700 dark:to-purple-700 rounded-3xl p-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-8 text-blue-50">
            Start your 30-day free trial today. No credit card required.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              onClick={() => handleGetStarted("trial")}
              variant="secondary"
              className="text-lg px-8 py-6 bg-white text-zinc-900 hover:bg-zinc-100"
            >
              Start Free Trial
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="text-lg px-8 py-6 border-white text-white hover:bg-white/10"
              onClick={handleContactSales}
            >
              Contact Sales
            </Button>
          </div>
          <p className="text-sm text-blue-100 mt-6">
            Questions? Email us at{" "}
            <a
              href="mailto:support@ithopu.com"
              className="underline hover:text-white"
            >
              support@ithopu.com
            </a>
          </p>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="container mx-auto px-4 pb-20">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 text-zinc-600 dark:text-zinc-400 mb-4">
            <Info className="w-5 h-5" />
            <span className="text-sm font-medium">Money-Back Guarantee</span>
          </div>
          <p className="text-sm text-zinc-500 dark:text-zinc-400">
            Try Ithopu risk-free. If you're not completely satisfied within the
            first 7 days, we'll refund your money, no questions asked.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
        <div className="container mx-auto px-4 py-12">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src={logo} alt="Ithopu" className="w-8 h-8" />
                <img src={iTHOPULogo} alt="Ithopu" className="h-7" />
              </div>
              <p className="text-zinc-600 dark:text-zinc-400 text-sm">
                Master mental mathematics with world-class techniques and
                interactive practice.
              </p>
            </div>

            <div>
              <h4 className="font-bold text-zinc-900 dark:text-white mb-4">
                Product
              </h4>
              <ul className="space-y-2 text-sm text-zinc-600 dark:text-zinc-400">
                <li>
                  <a
                    href="/#features"
                    className="hover:text-zinc-900 dark:hover:text-white transition-colors"
                  >
                    Features
                  </a>
                </li>
                <li>
                  <a
                    href="/pricing"
                    className="hover:text-zinc-900 dark:hover:text-white transition-colors"
                  >
                    Pricing
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className="hover:text-zinc-900 dark:hover:text-white transition-colors"
                  >
                    Techniques
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-zinc-900 dark:text-white mb-4">
                Resources
              </h4>
              <ul className="space-y-2 text-sm text-zinc-600 dark:text-zinc-400">
                <li>
                  <a
                    href="/blog"
                    className="hover:text-zinc-900 dark:hover:text-white transition-colors"
                  >
                    Blog
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className="hover:text-zinc-900 dark:hover:text-white transition-colors"
                  >
                    Tutorials
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className="hover:text-zinc-900 dark:hover:text-white transition-colors"
                  >
                    Support
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-zinc-900 dark:text-white mb-4">
                Company
              </h4>
              <ul className="space-y-2 text-sm text-zinc-600 dark:text-zinc-400">
                <li>
                  <a
                    href="#"
                    className="hover:text-zinc-900 dark:hover:text-white transition-colors"
                  >
                    About
                  </a>
                </li>
                <li>
                  <a
                    href="/contact"
                    className="hover:text-zinc-900 dark:hover:text-white transition-colors"
                  >
                    Contact
                  </a>
                </li>
                <li>
                  <a
                    href="/beta-testing"
                    className="hover:text-zinc-900 dark:hover:text-white transition-colors"
                  >
                    Join Beta
                  </a>
                </li>
                <li>
                  <a
                    href="/privacy"
                    className="hover:text-zinc-900 dark:hover:text-white transition-colors"
                  >
                    Privacy
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-zinc-200 dark:border-zinc-800 mt-8 pt-8 text-center text-sm text-zinc-600 dark:text-zinc-400">
            <p>&copy; 2026 Ithopu. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
